/**
 * sortDeliveryGroups — the feed's two-tier sort (#1289 C4).
 *
 * Tier 1 — base list: every group with an LS side (``matched``,
 * ``partial``, ``orphan_delivery``) stable-sorted by LS date
 * descending; groups with no usable LS date sink to the end.
 *
 * Tier 2 — orphan-GS insertion: ``orphan_settlement`` groups (pre-
 * sorted GS-date desc, undated last) each insert immediately BEFORE
 * the first element — scanning top→down — that is a LINKED group
 * (``matched``|``partial``), HAS a GS date (undated linked groups are
 * skipped, never anchor), and whose GS date is STRICTLY OLDER than the
 * orphan's. ``orphan_delivery`` rows and previously-inserted orphans
 * are transparent to the scan; without an anchor the orphan appends to
 * the end.
 *
 * In practice a GS is issued against its LS a few days later, so for
 * linked groups the GS-date order tracks the LS-date order and the
 * insertion lands where the dates suggest; the explicit strictly-older
 * rule keeps placement deterministic when a pair's LS/GS dates invert.
 *
 * Date accessors are deliveryGroupSummary's; comparison is on the raw
 * ISO strings (lexicographic == chronological), never on the
 * DD.MM.YYYY formatted output.
 */
import type { MatchGroup } from '../../../hooks/useMatchGroups';
import { gsDateValue, headerDateValue } from './deliveryGroupSummary';

const lsDateOf = (g: MatchGroup): string | undefined =>
  g.deliveryEvent ? headerDateValue('delivery', g.deliveryEvent) : undefined;

const gsDateOf = (g: MatchGroup): string | undefined =>
  g.settlementEvent ? gsDateValue(g.settlementEvent) : undefined;

/** Descending ISO compare; undefined (undated) sorts last. */
function descUndatedLast(a: string | undefined, b: string | undefined): number {
  if (a === b) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return a < b ? 1 : -1;
}

/** Explicit-index stable sort (JS sort is spec-stable since ES2019; the
 *  index pairing documents the intent and guards exotic runtimes). */
function stableSortBy(
  groups: MatchGroup[],
  keyOf: (g: MatchGroup) => string | undefined,
): MatchGroup[] {
  return groups
    .map((group, index) => ({ group, index }))
    .sort((x, y) => descUndatedLast(keyOf(x.group), keyOf(y.group)) || x.index - y.index)
    .map((p) => p.group);
}

export function sortDeliveryGroups(groups: MatchGroup[]): MatchGroup[] {
  const base: MatchGroup[] = [];
  const orphans: MatchGroup[] = [];
  for (const g of groups) {
    (g.status === 'orphan_settlement' ? orphans : base).push(g);
  }

  // Tier 1.
  const result = stableSortBy(base, lsDateOf);

  // Tier 2. The pre-sort guarantees (a) multiple orphans sharing an
  // anchor come out GS-date desc, and (b) months with only orphans
  // read date-ordered even though every one "appends to the end".
  for (const orphan of stableSortBy(orphans, gsDateOf)) {
    const orphanGsDate = gsDateOf(orphan);
    if (orphanGsDate === undefined) {
      result.push(orphan);
      continue;
    }
    const anchorIndex = result.findIndex((el) => {
      if (el.status !== 'matched' && el.status !== 'partial') return false;
      const elGsDate = gsDateOf(el);
      return elGsDate !== undefined && elGsDate < orphanGsDate;
    });
    if (anchorIndex === -1) {
      result.push(orphan);
    } else {
      result.splice(anchorIndex, 0, orphan);
    }
  }

  return result;
}
